import { useSyncExternalStore } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import axios from 'axios'

import { postRecordingImage, type RecordingImage } from './recordingImageApi'
import { recordingImagesKey } from './useRecordingImages'

// 撮った・選んだ 1 枚。送れたら一覧の行 (栄養の panel はタイル) に置き換わり、ここから外す
export type SendingItem = {
  localId: string
  yakurekiId: string
  blob: Blob
  status: 'sending' | 'failed'
}

export type SendResult =
  | { ok: true; image: RecordingImage }
  // reason はサーバが理由を付けて断ったとき (行は残さない)。null は送れなかった (行に残る)
  | { ok: false; reason: string | null }

// 記録ごとの送信中・送れなかった資料の置き場。部品の外に持つので、タブを替えても、録音の画面が閉じても、
// 記録を作った画面から移っても消えず、その記録の一覧 (タブ・節・panel) に出る。メモリにだけ持つので、1 枚でも残る間は
// ページを閉じる・再読み込みする前にブラウザの確認を出す (iPad の Safari は出さないことがある)。
let items: SendingItem[] = []
const listeners = new Set<() => void>()
let nextLocalId = 0

const confirmLeaving = (e: BeforeUnloadEvent) => {
  e.preventDefault()
  // 古いブラウザは returnValue を立てないと確認を出さない
  e.returnValue = ''
}

const setItems = (next: SendingItem[]) => {
  if (items.length === 0 && next.length > 0) {
    window.addEventListener('beforeunload', confirmLeaving)
  }
  if (items.length > 0 && next.length === 0) {
    window.removeEventListener('beforeunload', confirmLeaving)
  }
  items = next
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const withStatus = (localId: string, status: SendingItem['status']) =>
  setItems(items.map((i) => (i.localId === localId ? { ...i, status } : i)))

const without = (localId: string) =>
  setItems(items.filter((i) => i.localId !== localId))

// サーバが理由を付けて断った (形式・大きさ・枚数の 400)。再送しても通らない
const rejectionReason = (err: unknown): string | null => {
  if (!axios.isAxiosError(err) || err.response?.status !== 400) return null
  const reason = err.response.data?.error
  return typeof reason === 'string' ? reason : null
}

const send = async (
  queryClient: QueryClient,
  item: SendingItem
): Promise<SendResult> => {
  withStatus(item.localId, 'sending')
  try {
    const image = await postRecordingImage(item.yakurekiId, item.blob)
    // 一覧を引き直してから外す (送れた 1 枚が一覧に載る前に送信中の行が消えない)。
    // 送る前に始まった読み込みが途中なら、引き直しがそれに相乗りして送れた 1 枚が載らないので先に取り消す
    const queryKey = recordingImagesKey(item.yakurekiId)
    await queryClient.cancelQueries({ queryKey })
    await queryClient.invalidateQueries({ queryKey })
    without(item.localId)
    return { ok: true, image }
  } catch (err) {
    const reason = rejectionReason(err)
    if (reason) {
      without(item.localId)
      return { ok: false, reason }
    }
    withStatus(item.localId, 'failed')
    return { ok: false, reason: null }
  }
}

export const sendAttachment = (
  queryClient: QueryClient,
  yakurekiId: string,
  blob: Blob
): Promise<SendResult> => {
  nextLocalId += 1
  const item: SendingItem = {
    localId: `sending-${nextLocalId}`,
    yakurekiId,
    blob,
    status: 'sending',
  }
  setItems([...items, item])
  return send(queryClient, item)
}

export const retryAttachment = send

export const useAttachmentUploads = (): SendingItem[] =>
  useSyncExternalStore(subscribe, () => items)

// この記録に送っている最中の資料があるか。生成は押した時点の資料を読むので、送り終わるまで作る手を止める。
export const useIsUploadingAttachments = (yakurekiId: string | null) =>
  useSyncExternalStore(subscribe, () =>
    items.some((i) => i.yakurekiId === yakurekiId && i.status === 'sending')
  )

// 置き場を空にする (test の間で持ち越さない)
export const clearAttachmentUploads = () => setItems([])
